import type { PdpOrderBy } from '@vizit/api';
import type { ScoreLevel } from '@vizit/organisms';
import * as React from 'react';
import { ProductTableFilterOptions } from '../../components';
import { ProductTableContext } from './product-table-context';
import { DEFAULT_FILTER_STATE, ProductTableActionType } from './product-table-provider.state';
import { productTableFilterReducer } from './product-table-provider.state';
import type { ProductTableState } from './product-table-provider.state';

interface ProductTableProviderProps extends React.PropsWithChildren, ProductTableState {
  /**
   * The ID of the organization to fetch products for
   *
   * @required
   * @type {string}
   */
  organizationId: string;
  /**
   * The ID of the portfolio to fetch products for
   *
   * @optional
   * @type {string}
   */
  workspaceId?: string;
  /**
   * The function to call when the product table filters change
   *
   * @optional
   * @type {(filter: ProductTableFilterOptions, value: string) => void}
   * @param filter - The filter option to change
   * @param value - The new value for the filter
   */
  onProductTableFilterChange?: (filter: ProductTableFilterOptions, value?: string) => void;
}

/**
 * Provider component for the product table context.
 * Manages the state for product table filters, sorting, and pagination.
 *
 * @param props - The provider props
 */
function ProductTableProvider(props: ProductTableProviderProps) {
  const {
    children,
    workspaceId,
    organizationId,
    limit,
    sortBy,
    sortDirection,
    brand,
    productCategoryId,
    productCategoryName,
    listingScoreLevel,
    searchTerm,
    onProductTableFilterChange,
  } = props;

  const [filterState, dispatch] = React.useReducer(productTableFilterReducer, {
    limit: limit ?? DEFAULT_FILTER_STATE.limit,
    sortBy: sortBy ?? DEFAULT_FILTER_STATE.sortBy,
    sortDirection: sortDirection ?? DEFAULT_FILTER_STATE.sortDirection,
    brand: brand ?? DEFAULT_FILTER_STATE.brand,
    productCategoryId: productCategoryId ?? DEFAULT_FILTER_STATE.productCategoryId,
    productCategoryName: productCategoryName ?? DEFAULT_FILTER_STATE.productCategoryName,
    listingScoreLevel: listingScoreLevel ?? DEFAULT_FILTER_STATE.listingScoreLevel,
    searchTerm: searchTerm ?? DEFAULT_FILTER_STATE.searchTerm,
    retailer: props.retailer ?? DEFAULT_FILTER_STATE.retailer,
    experimentOnly: props.experimentOnly ?? DEFAULT_FILTER_STATE.experimentOnly,
  });

  const handleOnFilterBrand = React.useCallback(
    (brand: string) => {
      dispatch({ type: ProductTableActionType.SET_BRAND, payload: { brand } });

      onProductTableFilterChange?.(ProductTableFilterOptions.Brand, brand);
    },
    [onProductTableFilterChange],
  );

  const handleOnFilterProductCategory = React.useCallback(
    (productCategoryId: string, productCategoryName: string, categoryId?: string) => {
      dispatch({
        type: ProductTableActionType.SET_PRODUCT_CATEGORY_ID,
        payload: { productCategoryId, productCategoryName, categoryId: categoryId ?? null },
      });

      onProductTableFilterChange?.(ProductTableFilterOptions.ProductCategory, productCategoryId);
      onProductTableFilterChange?.(ProductTableFilterOptions.ProductCategoryName, productCategoryName);
    },
    [onProductTableFilterChange],
  );

  const handleOnFilterListingScoreLevel = React.useCallback(
    (listingScoreLevel: ScoreLevel) => {
      dispatch({ type: ProductTableActionType.SET_LISTING_SCORE_LEVEL, payload: { listingScoreLevel } });

      onProductTableFilterChange?.(ProductTableFilterOptions.ListingScoreLevel, listingScoreLevel);
    },
    [onProductTableFilterChange],
  );

  const handleOnSearchTermChange = React.useCallback(
    (value: string) => {
      dispatch({ type: ProductTableActionType.SET_SEARCH_TERM, payload: { searchTerm: value } });

      onProductTableFilterChange?.(ProductTableFilterOptions.Search, value);
    },
    [onProductTableFilterChange],
  );

  const handleOnFilterRetailer = React.useCallback(
    (retailer: string | undefined) => {
      dispatch({ type: ProductTableActionType.SET_RETAILER, payload: { retailer } });

      onProductTableFilterChange?.(ProductTableFilterOptions.Retailer, retailer);
    },
    [onProductTableFilterChange],
  );

  const handleOnFilterExperiment = React.useCallback(
    (enabled: boolean) => {
      dispatch({ type: ProductTableActionType.SET_EXPERIMENT_ONLY, payload: { experimentOnly: enabled } });

      onProductTableFilterChange?.(ProductTableFilterOptions.Experiment, enabled ? 'true' : undefined);
    },
    [onProductTableFilterChange],
  );

  const handleOnSort = React.useCallback(
    (field: PdpOrderBy['field']) => {
      if (filterState.sortBy !== field) {
        dispatch({ type: ProductTableActionType.SET_SORT_BY, payload: { sortBy: field } });
        dispatch({ type: ProductTableActionType.SET_SORT_DIRECTION, payload: { sortDirection: 'desc' } });
      } else if (filterState.sortDirection === 'desc') {
        dispatch({ type: ProductTableActionType.SET_SORT_DIRECTION, payload: { sortDirection: 'asc' } });
      } else {
        dispatch({ type: ProductTableActionType.SET_SORT_BY, payload: { sortBy: DEFAULT_FILTER_STATE.sortBy } });
        dispatch({
          type: ProductTableActionType.SET_SORT_DIRECTION,
          payload: { sortDirection: DEFAULT_FILTER_STATE.sortDirection },
        });
      }
    },
    [filterState.sortBy, filterState.sortDirection],
  );

  return (
    <ProductTableContext.Provider
      value={{
        ...filterState,
        workspaceId,
        organizationId,
        onFilterBrand: handleOnFilterBrand,
        onFilterProductCategory: handleOnFilterProductCategory,
        onFilterListingScoreLevel: handleOnFilterListingScoreLevel,
        onSearchTermChange: handleOnSearchTermChange,
        onFilterRetailer: handleOnFilterRetailer,
        onSort: handleOnSort,
        onFilterExperiment: handleOnFilterExperiment,
      }}
    >
      {children}
    </ProductTableContext.Provider>
  );
}

/**
 * Hook to access the product table context.
 * Must be used within a ProductTableProvider.
 *
 * @throws Error if used outside of ProductTableProvider
 * @returns The product table context
 */
const useProductTable = () => {
  const context = React.useContext(ProductTableContext);

  if (!context) {
    throw new Error('useProductTable must be used within a ProductTableProvider');
  }

  return context;
};

export { ProductTableProvider, useProductTable };
